import { UserService } from '@/services/users/UserService';
import { CloneDemoGenieParams } from '@/types/demo-genie';
import { AgentService } from '../agent/core';
import { DemoGenieService } from './DemoGenieService';

export class DemoGenieCloneService {
  private static STORAGE_KEY = "pending_demo_clone";

  /** Save clone intent to localStorage before auth redirect */
  static savePendingClone(params: CloneDemoGenieParams): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify({ ...params, timestamp: Date.now() }));
  }

  /** Check if there's a pending clone waiting to complete after auth */
  static hasPendingClone(): boolean {
    return localStorage.getItem(this.STORAGE_KEY) !== null;
  }

  /** Retrieve pending clone params from localStorage */
  static getPendingClone(): CloneDemoGenieParams | null {
    const raw = localStorage.getItem(this.STORAGE_KEY);
    if (!raw) return null;
    try {
      const { persona_id, custom_name, business_name } = JSON.parse(raw);
      return { persona_id, custom_name, business_name };
    } catch {
      return null;
    }
  }

  /** Clear pending clone from localStorage */
  static clearPendingClone(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Clone a demo genie to the authenticated user's account.
   * Since demo genies are regular agents (with is_demo_genie=true),
   */
  static async cloneToUserAccount(params: CloneDemoGenieParams): Promise<{
    agent: any;
    personaId: string;
  }> {
    // 1. Get current user
    console.log("[DemoClone] Starting clone for persona:", params.persona_id);
    const {
      data: { user },
    } = await UserService.getUser();
    if (!user) throw new Error("User not authenticated");
    console.log("[DemoClone] Authenticated as:", user.id);

    // 2. Get the demo agent from the agents table
    const demoAgent = await DemoGenieService.getDemoAgentByPersonaId(params.persona_id);
    if (!demoAgent) throw new Error(`Demo genie not found for persona: ${params.persona_id}`);
    if (!demoAgent.elevenlabs_id) throw new Error("Demo genie is not ready for cloning (no ElevenLabs ID)");
    console.log("[DemoClone] Found demo agent:", { id: demoAgent.id, name: demoAgent.name, elId: demoAgent.elevenlabs_id });

    // 3. Build the display name
    const baseName = demoAgent.name?.replace("[Demo] ", "") || "My Genie";
    const displayName = params.custom_name || (params.business_name ? `${baseName} - ${params.business_name}` : baseName);
    console.log("[DemoClone] Display name:", displayName);

    // 4. Build prompt replacements — swap demo brand name with user's business name
    const brandName = (demoAgent as any).branding?.brandName;
    const promptReplacements = params.business_name && brandName ? [{ from: brandName, to: params.business_name }] : undefined;
    if (promptReplacements) {
      console.log("[DemoClone] Will replace brand name in prompt:", { from: brandName, to: params.business_name });
    }

    // 5. Clone the agent
    const clonedAgent = await AgentService.duplicateAgent(demoAgent.id, displayName, promptReplacements);
    if (!clonedAgent) throw new Error("Failed to clone agent — no agent returned");

    console.log("[DemoClone] Clone successful:", clonedAgent?.id);
    return {
      agent: clonedAgent,
      personaId: params.persona_id,
    };
  }
}
